import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';

import PublicRoute from './PublicRoute';
import PrivateRoute from './PrivateRoute';

import SignInEmail from '../../screens/SignInEmail';
import SignInMobile from '../../screens/SignInMobile';
import SignUpEmail from '../../screens/SignUpEmail';
import SignUpMobile from '../../screens/SignUpMobile';
import Root from '../../screens/Root';
import Errors from '../../screens/Errors';
import MainSplash from '../../screens/MainSplash';
import ForgetPassword from '../../screens/ForgotPassword';
import ProfileScreen from '../../screens/Profile';
import MerchantScreen from '../../screens/Merchant';
import MessageScreen from '../../screens/Messages';
import AboutScreen from '../../screens/About';
import MoreOption from '../../screens/More';
import RedeemContactlessScreen from '../../screens/RedeemContactless';
import JoinMerchantScreen from '../../screens/JoinMerchant';
import RedeemCouponScreen from '../../screens/RedeemCoupon';
import ReferralScreen from '../../screens/Referral';
import SingleUseStatusScreen from '../../screens/RedeemSingleUseStatus';
import Programs from '../../screens/Programs';
import Scan from '../../screens/Scan';

import { client } from '../../api';
import useAuthContext from '../../hooks/useAuthContext';
import AuthLayout from '../Layout/AuthLayout';

/**
 * APIErrorHandler sets up api error handlers which need access to the router.
 */
function APIErrorHandler({ children }: React.PropsWithChildren<{}>) {
  const auth = useAuthContext();
  const history = useHistory();
  const [isReady, setIsReady] = useState(false);

  // Handle 401 Unauthorized and 403 Forbidden errors
  useEffect(() => {
    const interceptor = client.interceptors.response.use(
      (response) => response,
      (error) => {
        const { response } = error;

        switch (response.status) {
          case 401:
            // Logout the user
            auth?.removeSession();

            break;
          // Not sure if this needed
          // case 403:
          //   // Redirect to the root path
          //   history.replace('/');

          //   break;
        }

        return Promise.reject(error);
      },
    );

    setIsReady(true);

    return () => client.interceptors.response.eject(interceptor);
  }, [history, auth]);

  if (!isReady) {
    return null;
  }

  return <>{children}</>;
}

/**
 * AppRouter handles application level routing for the app.
 */
export default function AppRouter() {
  const auth = useAuthContext();

  // Don't render until the authentication context is ready
  if (!auth?.isReady) {
    return null;
  }

  return (
    <Router>
      <APIErrorHandler>
        <Switch>
          {/* Authentication Routes */}
          <PublicRoute path="/loginEmail">
            <SignInEmail />
          </PublicRoute>

          <PublicRoute path="/loginMobile">
            <SignInMobile />
          </PublicRoute>

          <PublicRoute path="/signupEmail">
            <SignUpEmail />
          </PublicRoute>

          <PublicRoute path="/signupMobile">
            <SignUpMobile />
          </PublicRoute>

          <PublicRoute path="/forgot_password">
            <ForgetPassword />
          </PublicRoute>

          {/* App Routes */}
          {auth && !auth.isAuthenticated && (
            <PublicRoute exact path="/">
              <MainSplash />
            </PublicRoute>
          )}

          {auth && auth.isAuthenticated && (
            <PrivateRoute path="/">
              <AuthRoutes />
            </PrivateRoute>
          )}

          {/* Error Route */}
          <Route>
            <Errors.NotFound />
          </Route>
        </Switch>
      </APIErrorHandler>
    </Router>
  );
}

function AuthRoutes() {
  let { path } = useRouteMatch();

  return (
    <AuthLayout>
      <Switch>
        <PrivateRoute exact path={`${path}`}>
          <Root />
        </PrivateRoute>

        <PrivateRoute path={`${path}profile`}>
          <ProfileScreen />
        </PrivateRoute>
        <PrivateRoute path={`${path}about`}>
          <AboutScreen />
        </PrivateRoute>

        <PrivateRoute exact path={`${path}merchant`}>
          <MerchantScreen />
        </PrivateRoute>

        <PrivateRoute exact path={`${path}message`}>
          <MessageScreen />
        </PrivateRoute>

        <PrivateRoute exact path={`${path}programs`}>
          <Programs />
        </PrivateRoute>

        <PrivateRoute exact path={`${path}more`}>
          <MoreOption />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}contactless/:contactlessCode/merchant/:merchantId/stampCard/:stampCardId/status/:status/msg/:msg`}
        >
          <RedeemContactlessScreen />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}coupon/:couponCode/merchant/:merchantId/status/:status/msg/:msg`}
        >
          <RedeemCouponScreen />
        </PrivateRoute>

        <PrivateRoute exact path={`${path}join_merchant/:merchantId`}>
          <JoinMerchantScreen />
        </PrivateRoute>

        <PrivateRoute exact path={`${path}scan`}>
          <Scan />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}referral/merchantCode/:merchantCode/userCode/:userCode`}
        >
          <ReferralScreen />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}merchant/:merchantId/stampNum/:stampNum/stampCard/:stampCardId/singleUseRedeem/status/:status/msg/:msg`}
        >
          <SingleUseStatusScreen />
        </PrivateRoute>
      </Switch>
    </AuthLayout>
  );
}
